import type { AxiosError } from 'axios';

/** Messages the backend returns with an error code, worded for the person reading them. */
const externalAuthMessages: Record<string, string> = {
    no_linked_account:
        'No Customily account is linked to this account. If you already have one, log in with your password; otherwise sign up first.',
    email_already_registered:
        'That email is already registered. Please log in with your password, or reset it from "Forgot your password".',
    store_already_registered: 'That store is already registered with another Customily account.',
    email_not_verified: 'Your email address is not verified with the provider.',
    email_required: 'An email address is required.',
    etsy_shop_required: 'This Etsy account has no shop. Open your Etsy shop first, then sign up with Etsy.',
    etsy_store_removed:
        'The Etsy store connected to your Customily account was removed. Please log in with your password, or reset it from "Forgot your password".',
    invalid_state: 'This sign in attempt expired. Please try again.',
    invalid_store_url: "That doesn't look like a valid URL for the selected platform. Please check it and try again.",
    invalid_ticket: 'This sign up expired. Please start again.',
    invalid_username: 'The username can only contain letters, numbers, dashes and underscores.',
    provider_error: 'We could not reach the provider to complete the sign in. Please try again.',
    store_required: 'An ecommerce URL is required to finish the sign up.',
    username_taken: 'That username is already taken. Please pick another one.',
    user_not_available: 'This account is not available. Please contact support.',
    verification_failed: 'We could not finish setting up your account. Please contact our support team.'
};

// Worded by the backend itself (taken username, registered store...), still something the user can act on
const EXPECTED_UNMAPPED_CODES = ['signup_failed'];

/** Codes the backend returns for situations the user can act on, which are not worth reporting. */
export const isExpectedAuthError = (error: unknown): boolean => {
    const code = responseData(error)?.code;
    return typeof code === 'string' && (code in externalAuthMessages || EXPECTED_UNMAPPED_CODES.includes(code));
};

// Amazon stores are keyed by store name, persisted as a domain
export const toRegistrationStoreUrl = (storeType: string, storeUrl: string): string =>
    storeType === 'Amazon' ? `${storeUrl}.amazon.com` : storeUrl;

/** The shapes our API errors come in: external auth codes, Web API messages and OWIN token errors. */
interface ApiErrorData {
    code?: string;
    message?: string;
    Message?: string;
    error_description?: string;
    error?: string;
}

const responseData = (error: unknown): ApiErrorData | undefined =>
    (error as AxiosError<ApiErrorData | undefined>)?.response?.data ?? undefined;

export const externalAuthErrorMessage = (error: unknown, fallback: string): string => {
    const data = responseData(error);
    if (data?.code && externalAuthMessages[data.code]) {
        return externalAuthMessages[data.code];
    }

    const message = data?.message || data?.Message || data?.error_description || data?.error;
    if (typeof message === 'string' && message.length) {
        return message;
    }

    // Popup cancelled, pop-up blocked and the like arrive as plain errors. Axios errors without a response
    //  ("Network Error", timeouts) are not worded for users, so they get the fallback instead
    if (error instanceof Error && error.message && !(error as AxiosError).isAxiosError) {
        return error.message;
    }

    return fallback;
};
